import React from 'react';

type Props = {
  onCredential: (credential: string) => void;
  buttonText?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  size?: 'large' | 'medium' | 'small';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  locale?: string;
  className?: string;
};

declare global {
  interface Window {
    google?: any;
  }
}

/**
 * Google Identity Services Button
 * - Loads https://accounts.google.com/gsi/client
 * - Renders the standard, compliant "Sign in with Google" button
 * - Emits the ID token credential via onCredential
 *
 * Notes:
 * - Requires VITE_GOOGLE_CLIENT_ID set in frontend .env
 */
const GoogleSignIn: React.FC<Props> = ({
  onCredential,
  buttonText = 'continue_with',
  size = 'medium',
  theme = 'filled_blue',
  shape = 'rounded',
  locale,
  className = '',
}) => {
  const btnRef = React.useRef<HTMLDivElement | null>(null);
  const [ready, setReady] = React.useState(false);

  // Resolve clientId with robust fallbacks and log source for diagnostics:
  // 1) Vite env (preferred)
  // 2) <meta name="google-client-id" content="..."/> in index.html
  // 3) window.__APP_ENV__ injected by an inline script in index.html
  function maskClientId(id?: string) {
    if (!id) return '(unset)';
    try {
      const s = String(id);
      if (s.length <= 12) return s;
      return `${s.slice(0, 6)}...${s.slice(-6)}`;
    } catch {
      return '(mask-failed)';
    }
  }
  function resolveClientId(): string | undefined {
    const vite = (import.meta as any)?.env?.VITE_GOOGLE_CLIENT_ID as string | undefined;
    if (vite && String(vite).trim()) {
      // eslint-disable-next-line no-console
      console.log('[GIS DEBUG] client_id source=vite', maskClientId(vite));
      return vite;
    }

    try {
      const meta = document?.querySelector('meta[name="google-client-id"]') as HTMLMetaElement | null;
      const metaVal = meta?.content;
      if (metaVal && metaVal.trim()) {
        // eslint-disable-next-line no-console
        console.log('[GIS DEBUG] client_id source=meta', maskClientId(metaVal));
        return metaVal.trim();
      }
    } catch {}

    const winAny: any = (window as any) || {};
    const injected = winAny.__APP_ENV__?.VITE_GOOGLE_CLIENT_ID as string | undefined;
    if (injected && injected.trim()) {
      // eslint-disable-next-line no-console
      console.log('[GIS DEBUG] client_id source=window.__APP_ENV__', maskClientId(injected));
      return injected.trim();
    }

    // eslint-disable-next-line no-console
    console.warn('[GIS DEBUG] No client_id resolved for GIS');
    return undefined;
  }

  const clientId = resolveClientId();

  // Keep a ref to the onCredential callback so the initialize() handler
  // doesn't need to re-run when the parent re-creates the function.
  const onCredentialRef = React.useRef(onCredential);
  React.useEffect(() => {
    onCredentialRef.current = onCredential;
  }, [onCredential]);

  React.useEffect(() => {
    if (!clientId) return;

    const ensureScript = () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        return Promise.resolve();
      }
      return new Promise<void>((resolve, reject) => {
        const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]') as HTMLScriptElement | null;
        if (existing) {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', () => reject(new Error('Failed to load Google Identity script')));
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://accounts.google.com/gsi/client';
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('Failed to load Google Identity script'));
        document.head.appendChild(s);
      });
    };

    let cancelled = false;
    ensureScript()
      .then(() => {
        if (cancelled) return;
        if (!window.google?.accounts?.id) return;

        // Use the ref's current value so we don't have to re-run this effect
        // every time the parent recreates the onCredential function.
        window.google.accounts.id.initialize({
          client_id: clientId,
          ux_mode: 'popup',
          callback: (response: { credential?: string }) => {
            if (response && response.credential) {
              try {
                onCredentialRef.current?.(response.credential);
              } catch {}
            }
          },
          auto_select: false,
          context: 'signin',
          locale: locale,
        });
        // eslint-disable-next-line no-console
        console.log('[GIS DEBUG] Initialized GIS with client_id', maskClientId(clientId));

        setReady(true);
      })
      .catch(() => {
        setReady(false);
      });

    return () => {
      cancelled = true;
      try {
        window.google?.accounts?.id?.cancel();
      } catch {}
    };
  }, [clientId, locale]);

  React.useEffect(() => {
    // Render the Google button once when GIS is ready and the container is available.
    // Avoid any continuous re-rendering or ResizeObservers because Google's renderButton
    // can modify layout and trigger feedback loops that cause repeated network calls.
    if (!ready) return;
    const el = btnRef.current;
    if (!el) return;

    try {
      // If already rendered (child present), do not call renderButton again.
      if (el.firstElementChild) return;

      const container = (el.parentElement ?? el) as HTMLElement;
      const parentWidth = (container?.clientWidth) || 320;
      const width = Math.max(220, Math.min(parentWidth, 520));

      window.google?.accounts?.id?.renderButton(el, {
        type: 'standard',
        theme,
        size,
        text: buttonText,
        shape,
        logo_alignment: 'left',
        width,
      });
    } catch {
      // ignore render errors
    }
    // Intentionally do not watch resize events to prevent render loops.
  }, [ready, buttonText, size, theme, shape]);

  if (!clientId) {
    return (
      <div className={`text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 ${className}`}>
        VITE_GOOGLE_CLIENT_ID is not configured.
      </div>
    );
  }

  return (
    <div className={`flex justify-center ${className}`}>
      <div ref={btnRef} aria-label="Sign in with Google" role="button" />

    </div>
  );
};

export default GoogleSignIn;